'use strict';
import React, { useState, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ChasseHuntMap, { createDivIcon } from '../components/ChasseHuntMap';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import { LangContext } from '../App';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// ── Couleurs par catégorie ────────────────────────────────────────────────────
const COULEURS = {
  migrateur: { bg: '#3b82f6', label: 'Migrateur', emoji: '🦆' },
  sedentaire: { bg: '#22c55e', label: 'Sédentaire', emoji: '🦌' },
  spot_peche: { bg: '#06b6d4', label: 'Pêche', emoji: '🐟' },
  etat_zone: { bg: '#f59e0b', label: 'État zone', emoji: '📍' },
  chasse: { bg: '#c9a227', label: 'Chasse pers.', emoji: '🏕️' },
};

function creerIcone(category, estChasse = false) {
  const clé = estChasse ? 'chasse' : category || 'etat_zone';
  const info = COULEURS[clé] || COULEURS.etat_zone;
  return createDivIcon({
    html: `<div style="
      width:32px;height:32px;border-radius:50% 50% 50% 0;
      background:${info.bg};border:2px solid #fff;
      transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.4);
      display:flex;align-items:center;justify-content:center;">
      <span style="transform:rotate(45deg);font-size:13px;line-height:1">${info.emoji}</span>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });
}

// ── Traductions ───────────────────────────────────────────────────────────────
const T = {
  fr: {
    title: 'Ma carte personnelle',
    sousTitre: 'Vos observations et sorties sur une carte interactive.',
    paywallTitre: 'Fonctionnalité Premium',
    paywallDesc:
      'La carte personnelle est available à partir du level Premium. Passez au level supérieur pour accéder à vos observations et chasses sur la carte.',
    paywallBtn: 'Voir mes crédits et passer Premium',
    filtres: 'Filtres',
    category: 'Catégorie',
    toutes: 'Toutes',
    species: 'Espèce',
    especePh: 'Orignal, Cerf…',
    periode: 'Depuis',
    periodes: {
      '': 'Toutes dates',
      7: '7 derniers jours',
      30: '30 derniers jours',
      90: '3 derniers mois',
      365: '1 an',
    },
    afficherObs: 'Observations communautaires',
    afficherChasses: 'Chasses personnelles',
    appliquer: 'Appliquer',
    chargement: 'Chargement de la carte…',
    erreur: 'Erreur lors du chargement des données.',
    aucunPoint: 'Aucune donnée avec ces filtres.',
    observations: 'observation(s)',
    chasses: 'sortie(s)',
    level: 'Niveau',
    expire: 'Expire le',
    date: 'Date',
    espece2: 'Espèce',
    legende: 'Légende',
  },
  en: {
    title: 'My Personal Map',
    sousTitre: 'Your observations and outings on an interactive map.',
    paywallTitre: 'Premium Feature',
    paywallDesc:
      'The personal map is available from the Premium tier. Upgrade to access your observations and hunts on the map.',
    paywallBtn: 'View my credits and go Premium',
    filtres: 'Filters',
    category: 'Category',
    toutes: 'All',
    species: 'Species',
    especePh: 'Moose, Deer…',
    periode: 'Since',
    periodes: {
      '': 'All dates',
      7: 'Last 7 days',
      30: 'Last 30 days',
      90: 'Last 3 months',
      365: 'Last year',
    },
    afficherObs: 'Community observations',
    afficherChasses: 'Personal hunts',
    appliquer: 'Apply',
    chargement: 'Loading map…',
    erreur: 'Error loading data.',
    aucunPoint: 'No data with these filters.',
    observations: 'observation(s)',
    chasses: 'outing(s)',
    level: 'Level',
    expire: 'Expires',
    date: 'Date',
    espece2: 'Species',
    legende: 'Legend',
  },
};

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page: { minHeight: '100vh', background: 'var(--couleur-fond)', color: 'var(--couleur-texte)' },
  contenu: { maxWidth: 1200, margin: '0 auto', paddingBottom: '4rem' },
  title: { fontSize: '1.6rem', fontWeight: 700, marginBottom: '0.3rem' },
  sousTitre: { color: 'var(--couleur-texte-attenué)', fontSize: '0.9rem', marginBottom: '1.2rem' },

  // Paywall
  paywall: {
    maxWidth: 520,
    margin: '5rem auto',
    textAlign: 'center',
    background: 'rgba(201,162,39,0.07)',
    border: '1px solid rgba(201,162,39,0.25)',
    borderRadius: 12,
    padding: '2.5rem 2rem',
  },
  paywallIcon: { fontSize: '3rem', marginBottom: '1rem' },
  paywallTitre: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: 'var(--couleur-or)',
    marginBottom: '0.8rem',
  },
  paywallDesc: {
    color: 'var(--couleur-texte-attenué)',
    lineHeight: 1.6,
    marginBottom: '1.5rem',
    fontSize: '0.95rem',
  },
  paywallBtn: {
    display: 'inline-block',
    padding: '0.8rem 1.8rem',
    background: 'var(--couleur-or)',
    color: '#000',
    borderRadius: 8,
    fontWeight: 700,
    cursor: 'pointer',
    border: 'none',
    fontSize: '0.95rem',
  },

  // Barre filtres
  barrefiltres: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.8rem',
    alignItems: 'flex-end',
    marginBottom: '1rem',
    background: 'var(--couleur-fond)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    padding: '1rem',
  },
  champFiltreWrap: { display: 'flex', flexDirection: 'column', gap: '0.3rem', minWidth: 130 },
  labelFiltre: { fontSize: '0.75rem', color: 'var(--couleur-texte-attenué)', fontWeight: 600 },
  select: {
    padding: '0.45rem 0.7rem',
    background: '#1a2535',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6,
    color: 'var(--couleur-texte)',
    fontSize: '0.88rem',
  },
  input: {
    padding: '0.45rem 0.7rem',
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6,
    color: 'var(--couleur-texte)',
    fontSize: '0.88rem',
    width: '100%',
    boxSizing: 'border-box',
  },
  checkWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  btnAppliquer: {
    padding: '0.5rem 1.2rem',
    background: 'var(--couleur-or)',
    color: '#000',
    border: 'none',
    borderRadius: 6,
    fontWeight: 700,
    fontSize: '0.85rem',
    cursor: 'pointer',
    alignSelf: 'flex-end',
  },

  // Compteurs
  compteurs: { display: 'flex', gap: '1rem', marginBottom: '0.8rem', flexWrap: 'wrap' },
  badge: {
    padding: '0.3rem 0.8rem',
    borderRadius: 20,
    fontSize: '0.8rem',
    fontWeight: 600,
  },

  // Carte
  carteWrap: {
    borderRadius: 12,
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.1)',
    height: 550,
  },

  // Légende
  legende: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.6rem',
    marginTop: '0.8rem',
  },
  legendeItem: { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem' },
  legendePuce: { width: 12, height: 12, borderRadius: '50%', flexShrink: 0 },

  msgCentré: { textAlign: 'center', padding: '4rem', color: 'var(--couleur-texte-attenué)' },
};

// ── Composant principal ───────────────────────────────────────────────────────
export default function PersonalMap() {
  const { lang } = useContext(LangContext);
  const { utilisateur } = useAuth();
  const navigate = useNavigate();
  const t = T[lang];
  const annule = useRef(false);

  // Niveau abonnement (récupéré depuis /me/credits pour être à jour)
  const [niveauAbonnement, setNiveauAbonnement] = useState(
    utilisateur?.subscription_tier || 'standard'
  );
  const estPremium = ['premium', 'premium_plus', 'pro'].includes(niveauAbonnement);

  // Filtres
  const [filtCategorie, setFiltCategorie] = useState('');
  const [filtEspece, setFiltEspece] = useState('');
  const [filtPeriode, setFiltPeriode] = useState('');
  const [showObs, setShowObs] = useState(true);
  const [showChasses, setShowChasses] = useState(true);
  // filtres appliqués (séparés pour éviter le re-fetch à chaque frappe)
  const [filtresAppliqués, setFiltresAppliqués] = useState({
    category: '',
    species: '',
    depuis: '',
    showObs: true,
    showChasses: true,
  });

  // Données
  const [observations, setObservations] = useState([]);
  const [chasses, setChasses] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');

  useEffect(
    () => () => {
      annule.current = true;
    },
    []
  );

  // Charger le level d'abonnement réel depuis le backend
  useEffect(() => {
    api
      .get('/me/credits')
      .then((r) => {
        if (!annule.current && r.data?.subscription_tier) {
          setNiveauAbonnement(r.data.subscription_tier);
        }
      })
      .catch(() => {});
  }, []);

  const chargerDonnees = useCallback(
    async (f) => {
      setChargement(true);
      setErreur('');
      try {
        const params = {};
        if (f.category) params.category = f.category;
        if (f.species) params.species = f.species;
        if (f.depuis) {
          const d = new Date();
          d.setDate(d.getDate() - parseInt(f.depuis, 10));
          params.depuis = d.toISOString();
        }

        const [resObs, resChasses] = await Promise.all([
          f.showObs
            ? api.get('/observations/mes-observations', { params })
            : Promise.resolve({ data: { data: [] } }),
          f.showChasses
            ? api.get('/me/chasses-personnelles', { params: { limite: 500 } })
            : Promise.resolve({ data: { chasses: [] } }),
        ]);

        if (!annule.current) {
          setObservations(resObs.data.data || []);
          // Filtrer les chasses avec coords
          const chassesAvecCoords = (resChasses.data.chasses || []).filter(
            (c) => c.lat != null && c.lng != null
          );
          setChasses(chassesAvecCoords);
        }
      } catch {
        if (!annule.current) setErreur(t.erreur);
      } finally {
        if (!annule.current) setChargement(false);
      }
    },
    [t.erreur]
  );

  useEffect(() => {
    if (estPremium) chargerDonnees(filtresAppliqués);
  }, [estPremium, filtresAppliqués, chargerDonnees]);

  function appliquerFiltres() {
    setFiltresAppliqués({
      category: filtCategorie,
      species: filtEspece,
      depuis: filtPeriode,
      showObs,
      showChasses,
    });
  }

  // Markers unifiés (observations + chasses perso) — calculé AVANT
  // l'early-return paywall pour préserver l'ordre des hooks (rules-of-hooks).
  // Les filtres show/showObs sont appliqués ici.
  const pointsObs = observations.filter((o) => o.lat != null && o.lng != null);
  const markersPersonnels = useMemo(() => {
    const fmtDate = (d) => new Date(d).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA');
    const obsMarkers = showObs
      ? pointsObs.map((obs) => ({
          id: `obs-${obs.id}`,
          lat: parseFloat(obs.lat),
          lng: parseFloat(obs.lng),
          icon: creerIcone(obs.category),
          popup: (
            <div style={{ minWidth: 180, fontSize: '0.85rem' }}>
              <strong style={{ fontSize: '1rem' }}>
                {COULEURS[obs.category]?.emoji} {obs.species || '—'}
              </strong>
              <div style={{ marginTop: '0.4rem', color: '#666' }}>
                {obs.category && (
                  <div>
                    {t.category} : {COULEURS[obs.category]?.label}
                  </div>
                )}
                {obs.credits_level_reached && (
                  <div>
                    {t.level} : {obs.credits_level_reached.replace('_', ' ')}
                  </div>
                )}
                {obs.expires_at && (
                  <div>
                    {t.expire} : {fmtDate(obs.expires_at)}
                  </div>
                )}
                <div>
                  {t.date} : {fmtDate(obs.createdAt)}
                </div>
              </div>
            </div>
          ),
        }))
      : [];
    const chasseMarkers = showChasses
      ? chasses.map((chasse) => ({
          id: `chasse-${chasse.id}`,
          lat: parseFloat(chasse.lat),
          lng: parseFloat(chasse.lng),
          icon: creerIcone('chasse', true),
          popup: (
            <div style={{ minWidth: 180, fontSize: '0.85rem' }}>
              <strong style={{ fontSize: '1rem' }}>🏕️ {chasse.species || t.chasses}</strong>
              {chasse.photo_url && (
                <div style={{ marginTop: '0.5rem' }}>
                  <img
                    src={chasse.photo_url}
                    alt=""
                    style={{
                      width: '100%',
                      borderRadius: 4,
                      maxHeight: 120,
                      objectFit: 'cover',
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <div style={{ marginTop: '0.4rem', color: '#666' }}>
                {chasse.activity_type && <div>{chasse.activity_type}</div>}
                <div>
                  {t.date} : {fmtDate(chasse.photo_taken_at || chasse.created_at)}
                </div>
              </div>
            </div>
          ),
        }))
      : [];
    return [...obsMarkers, ...chasseMarkers];
  }, [showObs, showChasses, pointsObs, chasses, lang, t]);

  // ── Paywall ────────────────────────────────────────────────────────────────
  if (!estPremium) {
    return (
      <PrivateLayout>
        <div style={S.contenu}>
          <div style={S.paywall}>
            <div style={S.paywallIcon}>🗺️</div>
            <h2 style={S.paywallTitre}>{t.paywallTitre}</h2>
            <p style={S.paywallDesc}>{t.paywallDesc}</p>
            <button style={S.paywallBtn} onClick={() => navigate('/my-credits')}>
              {t.paywallBtn}
            </button>
          </div>
        </div>
      </PrivateLayout>
    );
  }

  // tousLesPoints reste utilisé en-dessous pour les compteurs (badge
  // « 12 points ») — markersPersonnels (le tableau pour <ChasseHuntMap>)
  // vit plus haut, avant le paywall, par contrainte rules-of-hooks.
  const tousLesPoints = [
    ...pointsObs.map((o) => ({ lat: parseFloat(o.lat), lng: parseFloat(o.lng) })),
    ...chasses.map((c) => ({ lat: parseFloat(c.lat), lng: parseFloat(c.lng) })),
  ];

  return (
    <PrivateLayout>
      <div style={S.contenu}>
        <PageHeader icon="🗺️" title={t.title} subtitle={t.sousTitre} />

        {/* Filtres */}
        <div style={S.barrefiltres}>
          <div style={S.champFiltreWrap}>
            <span style={S.labelFiltre}>{t.category}</span>
            <select
              style={S.select}
              value={filtCategorie}
              onChange={(e) => setFiltCategorie(e.target.value)}
            >
              <option value="">{t.toutes}</option>
              {Object.entries(COULEURS)
                .filter(([k]) => k !== 'chasse')
                .map(([val, info]) => (
                  <option key={val} value={val}>
                    {info.emoji} {info.label}
                  </option>
                ))}
            </select>
          </div>

          <div style={S.champFiltreWrap}>
            <span style={S.labelFiltre}>{t.species}</span>
            <input
              style={S.input}
              type="text"
              value={filtEspece}
              onChange={(e) => setFiltEspece(e.target.value)}
              placeholder={t.especePh}
              onKeyDown={(e) => e.key === 'Enter' && appliquerFiltres()}
            />
          </div>

          <div style={S.champFiltreWrap}>
            <span style={S.labelFiltre}>{t.periode}</span>
            <select
              style={S.select}
              value={filtPeriode}
              onChange={(e) => setFiltPeriode(e.target.value)}
            >
              {Object.entries(t.periodes).map(([val, label]) => (
                <option key={val} value={val}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              alignSelf: 'flex-end',
            }}
          >
            <label style={S.checkWrap}>
              <input
                type="checkbox"
                checked={showObs}
                onChange={(e) => setShowObs(e.target.checked)}
              />
              {t.afficherObs}
            </label>
            <label style={S.checkWrap}>
              <input
                type="checkbox"
                checked={showChasses}
                onChange={(e) => setShowChasses(e.target.checked)}
              />
              {t.afficherChasses}
            </label>
          </div>

          <button style={S.btnAppliquer} onClick={appliquerFiltres}>
            {t.appliquer}
          </button>
        </div>

        {/* Compteurs */}
        {!chargement && !erreur && (
          <div style={S.compteurs}>
            {showObs && (
              <span style={{ ...S.badge, background: 'rgba(59,130,246,0.2)', color: '#93c5fd' }}>
                {pointsObs.length} {t.observations}
              </span>
            )}
            {showChasses && (
              <span
                style={{
                  ...S.badge,
                  background: 'rgba(201,162,39,0.2)',
                  color: 'var(--couleur-or)',
                }}
              >
                {chasses.length} {t.chasses}
              </span>
            )}
          </div>
        )}

        {/* Carte */}
        {chargement && <div style={S.msgCentré}>⏳ {t.chargement}</div>}
        {erreur && (
          <div style={{ ...S.msgCentré, color: 'var(--couleur-texte-erreur)' }}>{erreur}</div>
        )}

        {!chargement && !erreur && (
          <>
            {tousLesPoints.length === 0 ? (
              <div style={S.msgCentré}>{t.aucunPoint}</div>
            ) : (
              <div style={S.carteWrap}>
                {/* Observations + chasses fusionnées en un seul tableau de
                    markers — ChasseHuntMap se charge du fitToMarkers et de
                    l'OSM tile par défaut. Les filtres show/showObs sont
                    appliqués en amont via useMemo. */}
                <ChasseHuntMap
                  center={[46.5, -72.5]}
                  zoom={6}
                  basemap="osm"
                  height="100%"
                  fitToMarkers
                  markers={markersPersonnels}
                />
              </div>
            )}

            {/* Légende */}
            <div style={{ marginTop: '0.8rem' }}>
              <span
                style={{
                  fontSize: '0.78rem',
                  color: 'var(--couleur-texte-attenué)',
                  marginRight: '0.6rem',
                }}
              >
                {t.legende} :
              </span>
              <div style={S.legende}>
                {Object.entries(COULEURS).map(([key, info]) => (
                  <div key={key} style={S.legendeItem}>
                    <div style={{ ...S.legendePuce, background: info.bg }} />
                    <span style={{ fontSize: '0.78rem', color: 'var(--couleur-texte-attenué)' }}>
                      {info.emoji}{' '}
                      {lang === 'en' && key === 'sedentaire'
                        ? 'Resident'
                        : lang === 'en' && key === 'chasse'
                          ? 'Personal hunt'
                          : lang === 'en' && key === 'migrateur'
                            ? 'Migratory'
                            : lang === 'en' && key === 'spot_peche'
                              ? 'Fishing'
                              : lang === 'en' && key === 'etat_zone'
                                ? 'Zone status'
                                : info.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </PrivateLayout>
  );
}
